import PublicHeader from "./PublicHeader";
import React, { useEffect, useRef, useState } from "react";
import { signUp, confirmSignUp, resendSignUpCode, signIn, fetchAuthSession, signOut } from "@aws-amplify/auth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { inviteReturnPath } from "../../services/groupInvitations";
import { configureAuth } from "../../auth/authConfig";
import { persistAuthType } from "../../auth/authTypeStorage";
import { Eye, EyeOff } from "lucide-react";

const IS_FOUNDING_OPEN = true;
const PENDING_KEY = "bookish-author-signup";
const PROVISION_API = "https://n3evtdwzhd.execute-api.us-west-2.amazonaws.com/default/newauthoruserS3trigger";
const inputClass = "w-full px-4 py-2 min-h-[44px] border border-bookish-purple/15 rounded-lg text-sm bg-white focus:border-bookish-purple/40 focus:ring-2 focus:ring-bookish-purple/10 focus:outline-none";
const buttonClass = "w-full bg-bookish-royal hover:bg-bookish-purpleAccent text-white py-2 min-h-[44px] rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait";

function readPending() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(PENDING_KEY));
    return saved && typeof saved.email === "string" && saved.email ? saved : null;
  } catch { return null; }
}

function friendlyError(error) {
  const messages = {
    UsernameExistsException: "An account already uses this email. Log in, or choose ‘Finish verifying an existing account’ below.",
    InvalidPasswordException: "This password does not meet the account requirements. Try a longer password with uppercase and lowercase letters, a number, and a symbol.",
    CodeMismatchException: "That code doesn’t match. Check the latest email and try again.",
    ExpiredCodeException: "That code has expired. Request a new code below.",
    LimitExceededException: "Too many attempts. Please wait a little before trying again.",
    TooManyRequestsException: "Too many attempts. Please wait a little before trying again.",
    NotAuthorizedException: "We couldn’t sign you in. Check your password, or use the login page to reset it.",
    UserNotFoundException: "We couldn’t find that account. Check your email address or create an account.",
    UserNotConfirmedException: "Your email still needs verification. Choose ‘Enter a verification code’ below.",
    InvalidParameterException: "Please check your details and try again. If you already verified your email, choose ‘Continue setup’ below.",
  };
  return messages[error?.name] || "We couldn’t complete that step. Please try again. If it keeps happening, contact Lady Liza below.";
}

export default function SignUpAuthor() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pending] = useState(readPending);
  const groupReturn = inviteReturnPath(location.search) || inviteReturnPath(pending?.groupSearch || "");
  const loginPath = groupReturn ? `/loginauthor?returnTo=${encodeURIComponent(groupReturn)}` : "/loginauthor";
  const [step, setStep] = useState(location.state?.resumeVerification ? "resume" : pending ? (pending.confirmed === true ? "setup" : "verify") : "signup");
  const [email, setEmail] = useState(location.state?.resumeVerification && typeof location.state.email === "string" ? location.state.email : pending?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const lock = useRef(false);
  const heading = useRef(null);

  useEffect(() => { configureAuth("author"); }, []);
  useEffect(() => { if (step !== "signup") heading.current?.focus(); }, [step]);

  function remember(confirmed) {
    // Progress is only a UI hint. Authentication is still required before setup.
    // Never persist the password or verification code.
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({
        email: email.trim(), confirmed,
        groupSearch: groupReturn ? groupReturn.slice(groupReturn.indexOf("?")) : "",
      }));
    } catch { /* Signup also works when browser storage is unavailable. */ }
  }

  async function run(label, action) {
    if (lock.current) return;
    lock.current = true;
    setBusy(label);
    setError("");
    setMessage("");
    try {
      configureAuth("author");
      await action();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      lock.current = false;
      setBusy("");
    }
  }

  async function finishSetup() {
    setBusy("Opening your account…");
    persistAuthType("author");
    let result;
    try {
      result = await signIn({ username: email.trim(), password });
    } catch (err) {
      if (err.name !== "UserAlreadyAuthenticatedException") throw err;
      // Replace this browser's session without signing out other devices.
      await signOut();
      result = await signIn({ username: email.trim(), password });
    }
    if (!result.isSignedIn) {
      setError("Your email step is complete, but sign-in needs another step. Please continue through Author Login below.");
      return;
    }
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      setError("We couldn’t open your session. Retry setup, or continue through Author Login below.");
      return;
    }
    setBusy("Setting up your author home…");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(PROVISION_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({}),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("Provisioning failed");
    } catch {
      setError("Your email is verified, but we couldn’t finish setting up your author home. Retry setup below. You don’t need to create another account or verify again.");
      return;
    } finally {
      clearTimeout(timeout);
    }
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* Storage may be disabled. */ }
    setMessage(groupReturn ? "Opening your group invitation…" : "Opening your Author Portal…");
    // Login restores the shared application user state before entering the portal.
    navigate(loginPath, { replace: true });
  }

  function submit(event) {
    event.preventDefault();
    if (lock.current || !event.currentTarget.reportValidity()) return;
    if (step === "signup" && (!firstName.trim() || !lastName.trim())) {
      setError("Please enter your first and last name.");
      return;
    }
    if (step === "verify" && !verificationCode.trim()) {
      setError("Please enter the verification code from your email.");
      return;
    }
    run(step === "signup" ? "Creating your account…" : step === "verify" ? "Verifying…" : "Continuing…", async () => {
      if (step === "resume") {
        remember(false);
        setStep("verify");
        setMessage("Enter the code from your email, or request a new one below.");
      } else if (step === "signup") {
        const result = await signUp({
          username: email.trim(), password,
          options: { userAttributes: {
            email: email.trim(), name: `${firstName.trim()} ${lastName.trim()}`,
            "custom:firstname": firstName.trim(), "custom:lastname": lastName.trim(),
          } },
        });
        const confirmed = result.isSignUpComplete === true;
        remember(confirmed);
        setStep(confirmed ? "setup" : "verify");
        setMessage(confirmed ? "Your account is ready. Continue setup below." : "Check your email for your verification code. You’re one step closer to your author home.");
      } else if (step === "verify") {
        await confirmSignUp({ username: email.trim(), confirmationCode: verificationCode.trim() });
        remember(true);
        setStep("setup");
        await finishSetup();
      } else {
        await finishSetup();
      }
    });
  }

  function switchStep(next) {
    setError("");
    setMessage("");
    setVerificationCode("");
    setStep(next);
  }

  const signup = step === "signup";
  return (
    <div className="min-h-screen bg-bookish-cream text-bookish-royal">
      <PublicHeader audience="author" />
      <main className="account-page flex flex-col items-center">
        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-[0_12px_40px_rgba(63,37,107,0.10)] max-w-md w-full border border-bookish-purple/8">
          <p className="text-sm text-center uppercase tracking-widest mb-3">Your next chapter starts here</p>
          <h1 ref={heading} tabIndex={-1} className="text-3xl text-center font-serif font-bold mb-4 text-bookish-royal">
            {step === "verify" ? "Check your inbox" : step === "setup" ? "Let’s open your author home" : step === "resume" ? "Pick up where you left off" : groupReturn ? "Create Your Author Account" : IS_FOUNDING_OPEN ? "Claim Your Seat as a Founding Author" : "Create Author Account"}
          </h1>
          {signup && <>
            <p className="text-center text-sm mb-4">Create your account, verify your email, then set up your author home. You’ll choose your membership separately after signup.</p>
            {groupReturn ? <p className="mb-4 text-sm text-center">After verifying your email, accept the group invitation for $24.99/month or $240/year before applicable tax. Your Author account and shop stay independent.</p> : IS_FOUNDING_OPEN && <p className="text-center text-sm mb-5">Your founding membership includes <strong>30 days free</strong>, then $29.95/month or $288/year, billed annually (equivalent to $24/month). Cancel anytime. Founding authors lock in this rate.</p>}
          </>}
          {step === "verify" && <p className="text-sm mb-4 break-words">Enter the verification code sent to <strong>{email.trim()}</strong>. Check your spam folder if it hasn’t arrived.</p>}
          {step === "setup" && <p className="text-sm mb-4 break-words">Continue with <strong>{email.trim()}</strong> to finish account setup. You won’t need to enter another code if your email is already verified.</p>}
          <form onSubmit={submit} aria-label="Author signup" aria-busy={Boolean(busy)}>
            <fieldset disabled={Boolean(busy)} className="space-y-4 min-w-0">
              {signup && <>
                <div><label className="account-field" htmlFor="signupauthor-firstName">First name</label><input id="signupauthor-firstName" autoComplete="given-name" required value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClass} /></div>
                <div><label className="account-field" htmlFor="signupauthor-lastName">Last name</label><input id="signupauthor-lastName" autoComplete="family-name" required value={lastName} onChange={e => setLastName(e.target.value)} className={inputClass} /></div>
              </>}
              {(signup || step === "resume") && <div><label className="account-field" htmlFor="signupauthor-email">Email</label><input id="signupauthor-email" autoComplete="email" type="email" required value={email} onChange={e => setEmail(e.target.value.trim())} className={inputClass} /></div>}
              <div>
                <label className="account-field" htmlFor="signupauthor-password">Password</label>
                <p id="password-help" className="text-sm mb-2">{signup ? "Use a unique password with uppercase and lowercase letters, a number, and a symbol." : "Enter the password you chose when you created this account."}</p>
                <div className="relative">
                  <input id="signupauthor-password" autoComplete={signup ? "new-password" : "current-password"} type={showPassword ? "text" : "password"} required aria-describedby="password-help" value={password} onChange={e => setPassword(e.target.value)} className={`${inputClass} pr-14`} />
                  <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-bookish-royal"><span aria-hidden="true">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</span></button>
                </div>
              </div>
              {step === "verify" && <div><label className="account-field" htmlFor="signupauthor-verificationCode">Verification code</label><input id="signupauthor-verificationCode" autoComplete="one-time-code" inputMode="numeric" required value={verificationCode} onChange={e => setVerificationCode(e.target.value)} className={inputClass} /></div>}
              <div role="alert" className="text-sm text-bookish-royal">{error}</div>
              <p role="status" aria-live="polite" className="text-sm">{busy || message}</p>
              <button type="submit" className={buttonClass} disabled={Boolean(busy)}>{busy || (signup ? (IS_FOUNDING_OPEN && !groupReturn ? "Create Founding Author Account" : "Create Author Account") : step === "verify" ? "Verify email and continue" : step === "resume" ? "Continue verification" : "Retry / continue setup")}</button>
              {step === "verify" && <>
                <button type="button" className="w-full min-h-[44px] underline" onClick={() => run("Sending a new code…", async () => { await resendSignUpCode({ username: email.trim() }); setMessage("A new code is on its way. Use the most recent email to verify your account."); })}>Resend code</button>
                <button type="button" className="w-full min-h-[44px] underline" onClick={() => { remember(true); switchStep("setup"); }}>Already verified? Continue setup</button>
              </>}
              {step === "setup" && <button type="button" className="w-full min-h-[44px] underline" onClick={() => { remember(false); switchStep("verify"); }}>Enter a verification code</button>}
              <button type="button" className="w-full min-h-[44px] underline" onClick={() => switchStep(signup ? "resume" : step === "resume" ? "signup" : "resume")}>{signup ? "Finish verifying an existing account" : step === "resume" ? "Create a new account instead" : "Use a different email"}</button>
            </fieldset>
          </form>
          <p className="text-sm text-center mt-5">Already have an account? <Link to={loginPath} className="underline">Log in</Link></p>
          <Link to="/authors" className="block text-center py-3 underline">Back to For Authors</Link>
          <p className="text-sm mt-4">Read our <Link className="underline" to="/privacy">Privacy Policy</Link> and <Link className="underline" to="/terms">Terms</Link>. Questions? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
        </div>
      </main>
    </div>
  );
}
